import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, timeout: 30000 });

// Inject token if present
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('iv_admin_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Capability statement
export const capabilityMeta = () => api.get('/capability-statement/meta').then(r => r.data);
export const capabilityDownloadUrl = () => `${API}/capability-statement`;
export const adminUploadCapability = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/admin/capability-statement', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};

// Notification recipients
export const adminListRecipients = () => api.get('/admin/notification-recipients').then(r => r.data);
export const adminAddRecipient = (email) => api.post('/admin/notification-recipients', { email }).then(r => r.data);
export const adminRemoveRecipient = (email) => api.delete(`/admin/notification-recipients/${encodeURIComponent(email)}`).then(r => r.data);
export const adminTestNotification = () => api.post('/admin/notification-recipients/test').then(r => r.data);

// Public
export const createLead = (payload) => api.post('/leads', payload).then(r => r.data);
export const getStats = () => api.get('/stats').then(r => r.data);
export const getPublicJobs = () => api.get('/jobs').then(r => r.data);
export const submitApplication = (formData) => api.post('/applications', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
}).then(r => r.data);

// Admin auth
export const adminLogin = (username, password) => api.post('/admin/login', { username, password }).then(r => r.data);
export const adminMe = () => api.get('/admin/me').then(r => r.data);
export const adminUpdateCredentials = (payload) => api.put('/admin/credentials', payload).then(r => r.data);

// Users (owner only)
export const adminListUsers = () => api.get('/admin/users').then(r => r.data);
export const adminCreateUser = (payload) => api.post('/admin/users', payload).then(r => r.data);
export const adminDeleteUser = (id) => api.delete(`/admin/users/${id}`).then(r => r.data);
export const getSharedJobFields = () => api.get('/job-shared-fields').then(r => r.data);
export const adminUpdateSharedJobFields = (payload) => api.put('/admin/job-shared-fields', payload).then(r => r.data);
export const adminListLoginActivity = () => api.get('/admin/login-activity').then(r => r.data);
export const adminLatestSecurityScan = () => api.get('/admin/security-scans/latest').then(r => r.data);
export const adminRunSecurityScan = () => api.post('/admin/security-scans/run').then(r => r.data);
export const adminResetUserPassword = (id, new_password) => api.put(`/admin/users/${id}/password`, { new_password }).then(r => r.data);

// Admin jobs
export const adminListJobs = () => api.get('/admin/jobs').then(r => r.data);
export const adminCreateJob = (payload) => api.post('/admin/jobs', payload).then(r => r.data);
export const adminUpdateJob = (id, payload) => api.put(`/admin/jobs/${id}`, payload).then(r => r.data);
export const adminDeleteJob = (id) => api.delete(`/admin/jobs/${id}`).then(r => r.data);
export const adminDeleteLead = (id) => api.delete(`/admin/leads/${id}`).then(r => r.data);
export const adminDeleteApplication = (id) => api.delete(`/admin/applications/${id}`).then(r => r.data);
export const adminAnalyzeApplication = (id) => api.post(`/admin/applications/${id}/analyze`, null, { timeout: 90000 }).then(r => r.data);
export const adminGetApplicationAnalysis = (id) => api.get(`/admin/applications/${id}/analysis`).then(r => r.data);

// Standalone resume uploads (admin-initiated)
export const adminListResumeUploads = () => api.get('/admin/resume-uploads').then(r => r.data);
export const adminUploadResume = (formData) => api.post('/admin/resume-uploads', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: 60000,
}).then(r => r.data);
export const adminUploadResumesBatch = (formData) => api.post('/admin/resume-uploads/batch', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: 180000,
}).then(r => r.data);
export const adminDeleteResumeUpload = (id) => api.delete(`/admin/resume-uploads/${id}`).then(r => r.data);
export const adminAnalyzeResumeUpload = (id) => api.post(`/admin/resume-uploads/${id}/analyze`, null, { timeout: 90000 }).then(r => r.data);
export const fetchUploadedResumeBlob = async (id, filename) => {
  const res = await api.get(`/admin/resume-uploads/${id}/resume`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url; a.download = filename || 'resume';
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
};

// Resume repository
export const adminSearchResumes = (payload) => api.post('/admin/resume-repo/search', payload, { timeout: 60000 }).then(r => r.data);
export const adminResumeFacets = () => api.get('/admin/resume-repo/facets').then(r => r.data);
export const adminSetResumeHired = (id, hired) => api.post(`/admin/resume-repo/${encodeURIComponent(id)}/hired`, { hired }).then(r => r.data);
export const adminReindexResume = (id) => api.post(`/admin/resume-repo/${encodeURIComponent(id)}/reindex`).then(r => r.data);

// Admin submissions
export const adminListLeads = () => api.get('/leads').then(r => r.data);
export const adminListApplications = () => api.get('/applications').then(r => r.data);
export const resumeDownloadUrl = (appId) => {
  const token = localStorage.getItem('iv_admin_token') || '';
  return `${API}/applications/${appId}/resume?token=${encodeURIComponent(token)}`;
};

// Fetch resume as blob (uses Authorization header)
export const fetchResumeBlob = async (appId, filename) => {
  const res = await api.get(`/applications/${appId}/resume`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'resume';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
